import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import parse from 'html-react-parser';
import { formatImageUrl } from '../utils/helpers';
import { ArrowLeft, Calendar, FileText, Headphones } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/posts/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error('Failed to fetch post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="relative min-h-screen pt-24 pb-12">
        <canvas id="starfield" className="fixed inset-0 z-0 pointer-events-none"></canvas>
        <div className="relative z-10 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-200"></div>
            <p className="mt-4 text-gray-400">Betöltés...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <canvas id="starfield" className="fixed inset-0 z-0 pointer-events-none"></canvas>
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center gap-2 text-gray-400 hover:text-yellow-200 transition mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Vissza a bloghoz
        </button>

        <article className="glass rounded-2xl overflow-hidden">
          {/* Cover Image */}
          {post.cover_image && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={formatImageUrl(post.cover_image)}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Content */}
          <div className="p-8 md:p-12">
            {/* Meta Info */}
            <div className="flex items-center gap-4 mb-6">
              <span className={`px-3 py-1 border rounded-full text-xs font-semibold ${
                post.type === 'podcast'
                  ? 'bg-purple-500/20 border-purple-400/30 text-purple-300'
                  : 'bg-blue-500/20 border-blue-400/30 text-blue-300'
              }`}>
                {post.type === 'podcast' ? (
                  <><Headphones className="w-3 h-3 inline mr-1" /> Podcast</>
                ) : (
                  <><FileText className="w-3 h-3 inline mr-1" /> Blog</>
                )}
              </span>
              
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(post.created_at)}</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {post.title}
            </h1>

            {/* Excerpt */}
            {post.excerpt && (
              <p className="text-xl text-gray-300 mb-8 italic border-l-4 border-yellow-200/30 pl-4">
                {post.excerpt}
              </p>
            )}

            {/* Podcast Audio */}
            {post.type === 'podcast' && post.podcast_url && (
              <div className="mb-8 p-6 bg-purple-500/10 border border-purple-400/20 rounded-xl">
                <h3 className="text-lg font-semibold text-purple-200 mb-3 flex items-center gap-2">
                  <Headphones className="w-5 h-5" />
                  Hallgasd meg a podcast-ot
                </h3>
                <audio controls className="w-full">
                  <source src={post.podcast_url} type="audio/mpeg" />
                  A böngésződ nem támogatja az audio lejátszást.
                </audio>
              </div>
            )}

            {/* Content (Rich Text) */}
            <div className="blog-content">
              {parse(post.content)}
            </div>
          </div>
        </article>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
      `}</style>

      <style>{`
        .blog-content {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1.125rem;
          line-height: 1.8;
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        .blog-content h1,
        .blog-content h2,
        .blog-content h3 {
          color: white;
          font-weight: 600;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }

        .blog-content h1 {
          font-size: 2rem;
        }

        .blog-content h2 {
          font-size: 1.5rem;
        }

        .blog-content h3 {
          font-size: 1.25rem;
        }

        .blog-content p {
          margin-bottom: 1rem;
        }

        .blog-content strong {
          color: white;
          font-weight: 600;
        }

        .blog-content em {
          color: white;
          font-style: italic;
        }

        .blog-content a {
          color: rgb(254, 240, 138);
          text-decoration: underline;
        }

        .blog-content a:hover {
          color: rgb(254, 249, 195);
        }

        .blog-content ul,
        .blog-content ol {
          margin-left: 2rem;
          margin-bottom: 1rem;
          padding-left: 0;
        }

        .blog-content ul {
          list-style-type: disc;
        }

        .blog-content ol {
          list-style-type: decimal;
        }

        .blog-content li {
          margin-bottom: 0.5rem;
          margin-left: 1.5rem;
        }

        .blog-content blockquote {
          border-left: 4px solid rgb(254, 240, 138);
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: rgba(255, 255, 255, 0.7);
        }

        .blog-content code {
          background: rgba(0, 0, 0, 0.3);
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-family: monospace;
          color: rgb(196, 181, 253);
        }

        .blog-content pre {
          background: rgba(0, 0, 0, 0.3);
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1rem 0;
        }

        .blog-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1.5rem 0;
          display: block;
        }
      `}</style>
    </div>
  );
};

export default BlogPost;
